import { Drawer, DrawerOverlay, DrawerProps, useToast } from '@chakra-ui/react';
import {
  usePostWorkOrderMutation,
  usePostWorkOrderTransactionMutation,
  usePutWorkOrderMutation,
} from 'api';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { WorkOrderCreateFormDrawerContent } from './WorkOrderCreateFormDrawerContent';
import { WorkOrderUpdateFormDrawerContent } from './WorkOrderUpdateFormDrawerContent';
import { WorkOrderFormReviewDrawerContent } from './WorkOrderFormReviewDrawerContent';
import { WorkOrderFormRevisionDrawerContent } from './WorkOrderFormRevisionDrawerContent';
import { WorkOrderResponse } from 'api/bindings/WorkOrderResponse';
import { WorkOrderTransactionSummaryResponse } from 'api/bindings/WorkOrderTransactionSummaryResponse';
import { PostWorkOrderRequest } from 'api/bindings/PostWorkOrderRequest';
import { PutWorkOrderRequest } from 'api/bindings/PutWorkOrderRequest';
import { PostWorkOrderTransactionRequest } from 'api/bindings/PostWorkOrderTransactionRequest';

export enum WorkOrderWorkFlowStage {
  workOrder = 'workorder',
  revision = 'revision',
  review = 'review',
}

export interface WorkOrderFormWorkFlowDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  workOrder: WorkOrderResponse | undefined;
  initialStage?: WorkOrderWorkFlowStage;
}

type WorkOrderModel = PostWorkOrderRequest | PutWorkOrderRequest;
interface Form {
  header: WorkOrderModel;
  revision: PostWorkOrderTransactionRequest | undefined;
  currentStage: WorkOrderWorkFlowStage;
}

export const WorkOrderWorkFlowDrawer = (
  props: WorkOrderFormWorkFlowDrawerProps
) => {
  const {
    workOrder = {
      id: '0',
      afeId: '0',
      assigneeUserId: '0',
      commitmentDate: new Date().toLocaleString(),
      name: '',
      description: '',
      reference: '',
    },
    initialStage = WorkOrderWorkFlowStage.workOrder,
    ...rest
  } = props;

  // Determine if this is create or update based on whether work order has an id
  const isUpdate = workOrder?.id !== undefined && BigInt(workOrder.id) > 0;

  const [postWorkOrder] = usePostWorkOrderMutation();
  const [putWorkOrder] = usePutWorkOrderMutation();
  const [postRevision] = usePostWorkOrderTransactionMutation();

  const { control, setValue, handleSubmit, watch } = useForm<Form>({
    defaultValues: {
      header: workOrder,
      currentStage: initialStage,
    },
  });

  const [header, revision, currentStage] = watch([
    'header',
    'revision',
    'currentStage',
  ]);

  const toast = useToast();

  const afeId = useMemo(() => {
    if ('afeId' in header) {
      return header.afeId; // AFE ID is selected when posting a work order
    } else {
      return workOrder?.afeId ?? '0'; // We get AFE ID from a previous API response otherwise.
    }
  }, [header, workOrder?.afeId]);

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        let workOrderId = 'id' in header ? header.id : workOrder.id;

        if (initialStage === WorkOrderWorkFlowStage.workOrder) {
          if (BigInt(workOrderId) > 0) {
            const result = await putWorkOrder(
              form.header as PutWorkOrderRequest
            );
            if ('error' in result) {
              toast({
                title: `Failed to update work order ${form.header.name}`,
                status: 'error',
              });
            }
            toast({
              title: `Work Order ${form.header.name} updated!`,
              status: 'success',
            });
          } else {
            const result = await postWorkOrder({
              ...(form.header as PostWorkOrderRequest),
              assigneeUserId: form.header.assigneeUserId || '0',
            });
            if ('error' in result) {
              toast({
                title: `Failed to post work order ${form.header.name}`,
                status: 'error',
              });
              throw result.error;
            }
            workOrderId = result.data.id;
            toast({
              title: `Work Order ${form.header.name} posted!`,
              status: 'success',
            });
          }
        }
        if (form.revision) {
          const result = await postRevision({
            entityId: workOrderId,
            userId: null,
            name: form.revision.name,
            accounts: form.revision.accounts,
          });
          if ('error' in result) {
            toast({
              title: `Failed to post revision ${form.revision.name} to work order ${form.header.name}`,
              status: 'error',
            });
            throw result.error;
          }
          toast({
            title: `Revision ${form.revision.name} posted to work order ${form.header.name}`,
            status: 'success',
          });
        }

        props.onClose();
      } catch {
        // TODO
      }
    },
    [
      header,
      workOrder.id,
      initialStage,
      props,
      postWorkOrder,
      toast,
      putWorkOrder,
      postRevision,
    ]
  );

  const component = useMemo(() => {
    switch (currentStage) {
      case WorkOrderWorkFlowStage.workOrder:
        return (
          <Controller
            control={control}
            name="header"
            render={(p) => {
              if (isUpdate) {
                return (
                  <WorkOrderUpdateFormDrawerContent
                    afeId={afeId}
                    workOrder={workOrder}
                    onClose={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? WorkOrderWorkFlowStage.revision
                        : WorkOrderWorkFlowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              } else {
                return (
                  <WorkOrderCreateFormDrawerContent
                    onClose={props.onClose}
                    onSubmit={(f) => {
                      p.field.onChange(f);
                      const nextStage = f.includeRevision
                        ? WorkOrderWorkFlowStage.revision
                        : WorkOrderWorkFlowStage.review;
                      setValue('currentStage', nextStage);
                    }}
                  />
                );
              }
            }}
          />
        );
      case WorkOrderWorkFlowStage.revision:
        return (
          <Controller
            control={control}
            name="revision"
            render={(p) => (
              <WorkOrderFormRevisionDrawerContent
                afeId={afeId}
                workOrderName={header.name ?? ''}
                onClose={props.onClose}
                onSubmit={(f) => {
                  p.field.onChange(f);
                  setValue('currentStage', WorkOrderWorkFlowStage.review);
                }}
              />
            )}
          />
        );
      case WorkOrderWorkFlowStage.review:
        return (
          <WorkOrderFormReviewDrawerContent
            afeId={afeId}
            initialStage={initialStage}
            workOrder={header}
            revision={revision}
            onClose={props.onClose}
            onSubmit={() => {
              return handleSubmit(onSubmit)();
            }}
          />
        );
      default:
        return <></>;
    }
  }, [
    currentStage,
    control,
    afeId,
    initialStage,
    header,
    revision,
    props.onClose,
    isUpdate,
    workOrder,
    setValue,
    handleSubmit,
    onSubmit,
  ]);

  return (
    <Drawer size="xl" {...rest}>
      <DrawerOverlay />
      {component}
    </Drawer>
  );
};
